import { Box, Divider } from "@mui/joy";
import { useForm, FormProvider } from "react-hook-form";
import { useRef, useState, useEffect } from "react";

import { buildEmptyMeasurements } from "../../constants/roofMeasurements";
import {
    InspectionPhotos,
    RoofMeasurements,
    ShingleAndRoofing,
    UnderlaymentAndShield,
    Ventilation,
    Penetrations,
    DripEdgeFlashingValley,
    Chimneys,
    Skylights,
    Equipment,
    GuttersAndDownspouts,
    AncillaryAndTrim,
    Documents,
    InitialInspectionSkeleton,
    AutoSaveStatusIndicator,
} from "./components";
import { TaskActionComponent, TaskHeader } from "../Tasks/components";
import { useSync, useInit, useAutoSave } from "./hooks";
import { useNotification } from "../../hooks";

/** Stored Action value for FormActionSelect option "R&R". */
const DEFAULT_INSPECTION_ACTION = "Replace";

function withDefaultActions<T extends Record<string, any>>(values: T): T {
    const next: Record<string, any> = { ...values };

    for (const [key, value] of Object.entries(next)) {
        if (key.endsWith("Action") && (value === "" || value == null)) {
            next[key] = DEFAULT_INSPECTION_ACTION;
        }
    }

    if (Array.isArray(next.FurnaceVents)) {
        next.FurnaceVents = next.FurnaceVents.map((row: any) => ({
            ...row,
            Action:
                row?.Action === "" || row?.Action == null
                    ? DEFAULT_INSPECTION_ACTION
                    : row.Action,
        }));
    }

    return next as T;
}

const defaultValues = {
    Measurements: buildEmptyMeasurements(),
    ShingleType: "",
    RoofColor: "",
    TearOff: "",
    UnderlaymentType: "",
    IceWaterBarrierSQ: "",
    IceWaterBarrierAction: DEFAULT_INSPECTION_ACTION,
    Starter: "",
    Valley: "",
    RidgeCapProfile: "",
    Stories: "",
    LowSlopeSQ: "",
    HighRoofSQ: "",
    RoofPitch: [{ Name: "", Pitch: "", Squares: "" }],
    VentilationType: "",
    BoxVentQty: "",
    BoxVentAction: DEFAULT_INSPECTION_ACTION,
    TurbineVentQty: "",
    TurbineVentAction: DEFAULT_INSPECTION_ACTION,
    VentilationPainted: "",
    BathVentSize: "",
    BathVentQty: "",
    BathVentAction: DEFAULT_INSPECTION_ACTION,
    DryerVentSize: "",
    DryerVentQty: "",
    DryerVentAction: DEFAULT_INSPECTION_ACTION,
    FurnaceVents: [{ Size: "", Qty: "", Action: DEFAULT_INSPECTION_ACTION }],
    PlumbingJacksQty: "",
    PlumbingJacksAction: DEFAULT_INSPECTION_ACTION,
    SplitBootsQty: "",
    SplitBootsAction: DEFAULT_INSPECTION_ACTION,
    EaveDripEdge: "",
    RakeDripEdge: "",
    ValleyType: "",
    StepFlashingQty: "",
    StepFlashingAction: DEFAULT_INSPECTION_ACTION,
    CounterFlashingSize: "",
    CounterFlashingQty: "",
    CounterFlashingAction: DEFAULT_INSPECTION_ACTION,
    HeadWallMetalQty: "",
    HeadWallMetalAction: DEFAULT_INSPECTION_ACTION,
    KickoutDiverterQty: "",
    KickoutDiverterAction: DEFAULT_INSPECTION_ACTION,
    Chimneys: [
        { Type: "Brick", Size: 'Average (32"x36")', Color: "", Count: 1 },
    ],
    Skylights: [
        {
            Type: "None",
            Size: "None",
            FlashingKit: "N/A",
            Count: 1,
            Notes: "",
        },
    ],
    EvaporativeCooler: "",
    GutterSize: "",
    GutterColor: "",
    GutterLengthQty: "",
    GutterLengthAction: DEFAULT_INSPECTION_ACTION,
    DownspoutSize: "",
    DownspoutColor: "",
    DownspoutLengthQty: "",
    DownspoutLengthAction: DEFAULT_INSPECTION_ACTION,
    DirectionalElbowStyleAQty: "",
    DirectionalElbowStyleBQty: "",
    OffsetQty: "",
    GutterNotes: "",
    GutterGuardType: "",
    PaintGutterQty: "",
    PaintGutterAction: DEFAULT_INSPECTION_ACTION,
    CorniceReturnsQty: "",
    CorniceReturnsAction: DEFAULT_INSPECTION_ACTION,
    CorniceStripQty: "",
    CorniceStripAction: DEFAULT_INSPECTION_ACTION,
    FasciaBoardQty: "",
    FasciaBoardAction: DEFAULT_INSPECTION_ACTION,
    SoffitInches: "",
    GutterApronQty: "",
    GutterApronAction: DEFAULT_INSPECTION_ACTION,
    SheathingQty: "",
    SheathingAction: DEFAULT_INSPECTION_ACTION,
};

function FormSections({
    taskId,
    projectId,
}: {
    taskId: string;
    projectId: string;
}) {
    return (
        <Box>
            <InspectionPhotos taskId={taskId} projectId={projectId} />
            <Divider sx={{ my: 3 }} />
            <ShingleAndRoofing />
            <Divider sx={{ my: 3 }} />
            <UnderlaymentAndShield />
            <Divider sx={{ my: 3 }} />
            <Ventilation />
            <Divider sx={{ my: 3 }} />
            <Penetrations />
            <Divider sx={{ my: 3 }} />
            <DripEdgeFlashingValley />
            <Divider sx={{ my: 3 }} />
            <Chimneys />
            <Divider sx={{ my: 3 }} />
            <Skylights />
            <Divider sx={{ my: 3 }} />
            <GuttersAndDownspouts />
            <Divider sx={{ my: 3 }} />
            <Equipment />
            <Divider sx={{ my: 3 }} />
            <AncillaryAndTrim />
            <Divider sx={{ my: 3 }} />
            <RoofMeasurements />
            <Divider sx={{ my: 3 }} />
            <Documents />
        </Box>
    );
}

export default function InitialInspection({ activeTask, onComplete }: any) {
    const { Id, ProjectId } = activeTask || {};
    const { showError } = useNotification();

    const methods = useForm({
        defaultValues: defaultValues,
    });

    const { syncEngine, syncing } = useSync();
    const skipNextAutoSaveRef = useRef(false);
    const [autoSaveReady, setAutoSaveReady] = useState(false);

    const { baselineAttributes, setBaselineAttributes, loading } = useInit({
        projectId: ProjectId,
        onSuccess: (flatData) => {
            methods.reset(withDefaultActions({ ...defaultValues, ...flatData }));
            skipNextAutoSaveRef.current = true;
            setAutoSaveReady(true);
        },
    });

    const { saveStatus, flushSave } = useAutoSave({
        enabled: autoSaveReady && !loading,
        projectId: ProjectId,
        watch: methods.watch,
        getValues: methods.getValues,
        baselineAttributes,
        setBaselineAttributes,
        syncEngine,
        skipNextSaveRef: skipNextAutoSaveRef,
        onError: (message) => showError(message),
    });

    useEffect(() => {
        if (!loading && ProjectId && !autoSaveReady) {
            skipNextAutoSaveRef.current = true;
            setAutoSaveReady(true);
        }
    }, [loading, ProjectId, autoSaveReady]);

    const handleComplete = async () => {
        const result = await flushSave();
        if (!result?.success) {
            return;
        }

        if (onComplete) {
            onComplete();
        }
    };

    const isSaving = syncing || saveStatus === "saving";

    return (
        <FormProvider {...methods}>
            <Box sx={{ position: "relative", minHeight: 400 }}>
                {loading ? (
                    <Box
                        sx={{
                            animation: "fadeIn 0.3s ease-in-out",
                            px: { xs: 2, sm: 3 },
                        }}
                    >
                        <InitialInspectionSkeleton />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            mt: 1,
                            px: { xs: 2, sm: 3 },
                            animation: "fadeIn 0.5s ease-in-out",
                            "@keyframes fadeIn": {
                                "0%": { opacity: 0 },
                                "100%": { opacity: 1 },
                            },
                        }}
                    >
                        <TaskHeader activeTask={activeTask} />
                        <AutoSaveStatusIndicator status={saveStatus} />
                        <Divider />
                        <FormSections
                            taskId={Id || ""}
                            projectId={ProjectId || ""}
                        />

                        <Box mt={4}>
                            <Divider />
                            <TaskActionComponent
                                taskName={activeTask?.Name || ""}
                                isNextEligible={true}
                                actionProgress={{ loading: isSaving }}
                                onComplete={handleComplete}
                                taskId={Id || ""}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </FormProvider>
    );
}
